/**
 * THE FIXTURE TRANSPORT — what stands in for the API until A1 and A2 implement these operations.
 *
 * It is a TRANSPORT, not a mocked client: it answers with a status and a JSON body exactly as the
 * network would, so the typed client (./operations.ts), the runtime shape checks, the error mapping
 * and every screen state above it are the REAL ones and are exercised today. Deleting this file and
 * pointing `ACTIVE_TRANSPORT` at `createFetchTransport` changes nothing above the seam.
 *
 * WHY THERE ARE SCENARIOS RATHER THAN ONE HAPPY PATH. Four of this feature's states are states the
 * corpus says will really happen and that a developer would otherwise never see: both addresses
 * absent on a fresh box (asserted by A1's acceptance script step 7,
 * `docs/design/12-build-plan.md`), a 403 on the contacts read for a caller without the
 * author capability, a 503 on the terms read, and a material change that forces re-acceptance. An
 * empty state nobody has looked at is a blank, and a blank is a bug.
 *
 * Pick one with `VITE_SUPPORT_FIXTURE=<scenario>` in `.env.development`.
 */
import type { AgreementList, AuthorSession, PlatformContacts, TermsState } from './contract';
import { API_PATHS } from './contract';
import type { ApiCall, ApiRawResponse, ApiTransport } from './transport';
import { ApiError } from './errors';

export type FixtureScenario =
  /** The ordinary steady state: both addresses set, terms published and already accepted. */
  | 'ready'
  /** A freshly deployed box before the named M0 step sets the addresses, and before A2 publishes a terms row. */
  | 'fresh-box'
  /** Support set, editorial not. Branch on the VALUE, never on key presence. */
  | 'one-address'
  /**
   * A material change was published, and the editorial standards (design 25) were never accepted:
   * `termsAcceptanceRequired`, and sending an article for review is held until both are.
   */
  | 'reaccept'
  /** The contacts read is refused for this caller; the guides and the terms are not. */
  | 'forbidden'
  /** Nothing reaches the API at all. */
  | 'offline';

const SCENARIOS: readonly FixtureScenario[] = [
  'ready',
  'fresh-box',
  'one-address',
  'reaccept',
  'forbidden',
  'offline',
];

/** How long a fixture call takes, so loading states are visible rather than theoretical. */
const FIXTURE_LATENCY_MS = 320;

export function resolveFixtureScenario(): FixtureScenario {
  const configured: unknown = import.meta.env.VITE_SUPPORT_FIXTURE;
  if (typeof configured === 'string') {
    const match = SCENARIOS.find((scenario) => scenario === configured);
    if (match !== undefined) {
      return match;
    }
  }
  return 'ready';
}

/**
 * Addresses are the operator's and are `NULL` until set at deployment. These two are plainly
 * fictional on purpose — a fixture that looks like a real address is the thing that eventually
 * ships to a reader.
 */
const FIXTURE_SUPPORT_EMAIL = 'support@example.invalid';
const FIXTURE_EDITORIAL_EMAIL = 'editorial@example.invalid';

function contactsFor(scenario: FixtureScenario): PlatformContacts {
  switch (scenario) {
    case 'fresh-box':
      // The row exists; the addresses do not. This is the shipped, expected M0 state.
      return { supportEmail: null, editorialEmail: null };
    case 'one-address':
      return { supportEmail: FIXTURE_SUPPORT_EMAIL, editorialEmail: null };
    default:
      return { supportEmail: FIXTURE_SUPPORT_EMAIL, editorialEmail: FIXTURE_EDITORIAL_EMAIL };
  }
}

const FIXTURE_TERMS_URL = 'https://moonx.app/creators/terms';

function initialTermsFor(scenario: FixtureScenario): TermsState {
  if (scenario === 'reaccept') {
    return {
      versionLabel: '2026-09',
      effectiveFrom: '2026-09-01T00:00:00Z',
      contentUrl: FIXTURE_TERMS_URL,
      isMaterialChange: true,
      accepted: false,
      acceptedAt: null,
      blocksSubmission: true,
    };
  }
  return {
    versionLabel: '2026-01',
    effectiveFrom: '2026-01-01T00:00:00Z',
    contentUrl: FIXTURE_TERMS_URL,
    isMaterialChange: false,
    accepted: true,
    acceptedAt: '2026-02-11T09:14:00Z',
    blocksSubmission: false,
  };
}

/** `GET /v1/me`. Typed against the contract so a fixture cannot drift from the schema. */
function sessionFor(terms: TermsState, standardsAccepted: boolean): AuthorSession {
  return {
    authorId: 'author-fixture',
    displayName: 'Fixture Author',
    authorStatus: 'approved',
    canApply: false,
    canUseEditor: true,
    cabinetReachable: true,
    termsAcceptanceRequired: !terms.accepted || !standardsAccepted,
    slug: 'fixture-author',
    slugFrozen: true,
  };
}

function delay(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      resolve();
    }, ms);
    if (signal !== undefined) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new ApiError('network', 0, null, 'The request was cancelled.'));
        },
        { once: true },
      );
    }
  });
}

function problem(status: number, code: string, title: string, detail: string): ApiRawResponse {
  return {
    status,
    body: {
      type: `https://moonx.art/problems/${code.toLowerCase().replaceAll('_', '-')}`,
      title,
      status,
      detail,
      error_code: code,
    },
  };
}

/**
 * Builds a transport for one scenario. The terms record is MUTABLE inside the closure so that
 * accepting on the interstitial really does flip `accepted` — a fixture where the button changes
 * nothing cannot show you that the success path works.
 */
export function createFixtureTransport(scenario: FixtureScenario): ApiTransport {
  let terms = initialTermsFor(scenario);
  // Design 25: the editorial standards, owed only where a change is waiting.
  let standardsAccepted = scenario !== 'reaccept';

  return async (call: ApiCall): Promise<ApiRawResponse> => {
    await delay(FIXTURE_LATENCY_MS, call.signal);

    if (scenario === 'offline') {
      throw new ApiError('network', 0, null, 'The fixture transport is in its offline scenario.');
    }

    if (call.method === 'GET' && call.path === API_PATHS.authorSession) {
      return { status: 200, body: sessionFor(terms, standardsAccepted) };
    }

    if (call.method === 'GET' && call.path === API_PATHS.platformContacts) {
      if (scenario === 'forbidden') {
        // A bare 403 in the spec — it names no error code, so neither does this. A screen that
        // switched on a code here would be inventing contract.
        return problem(
          403,
          'AUTHOR_CAPABILITY_REQUIRED',
          'Verified, but not permitted to do this.',
          'This caller may not read the platform contact addresses.',
        );
      }
      return { status: 200, body: contactsFor(scenario) };
    }

    if (call.method === 'GET' && call.path === API_PATHS.currentTerms) {
      if (scenario === 'fresh-box') {
        // No `terms_version` row exists until A2's scripted deployment step publishes the first
        // one, and the operation declares 200/401/503 with no 404. 503 is the only declared answer
        // that fits, and the page must degrade rather than blank.
        return problem(
          503,
          'TRANSITION_NOT_LEGAL',
          'Service unavailable.',
          'No terms version has been published yet.',
        );
      }
      return { status: 200, body: terms };
    }

    if (call.method === 'POST' && call.path === API_PATHS.termsAcceptances) {
      const body = call.body;
      const versionLabel =
        typeof body === 'object' && body !== null
          ? (body as Record<string, unknown>).versionLabel
          : undefined;

      if (typeof versionLabel !== 'string' || versionLabel.length === 0) {
        return problem(
          400,
          'APPLICATION_INCOMPLETE',
          'Bad request.',
          'versionLabel is required and must be a non-empty string.',
        );
      }
      if (versionLabel !== terms.versionLabel) {
        return problem(
          404,
          'WRONG_FROM_STATE',
          'No such terms version, or it is superseded.',
          'A newer version has been published.',
        );
      }
      if (terms.accepted) {
        // Two tabs, or a double click that got through. The screens treat this as SUCCESS.
        return problem(
          409,
          'VERDICT_ALREADY_RECORDED',
          'This author has already accepted this version.',
          'Double acceptance is impossible by construction.',
        );
      }

      // The server writes `accepted_via = 'cabinet_reaccept'`; the client neither sends nor sees it.
      terms = {
        ...terms,
        accepted: true,
        acceptedAt: '2026-08-27T12:00:00Z',
        blocksSubmission: false,
      };
      return { status: 201, body: terms };
    }

    if (call.method === 'GET' && call.path === API_PATHS.agreements) {
      return { status: 200, body: agreementsFor(scenario, terms, standardsAccepted) };
    }

    if (call.method === 'POST' && call.path === API_PATHS.agreementAcceptances) {
      const accept = acceptItemsOf(call.body);
      if (accept === null || accept.length < 1 || accept.length > 4) {
        return problem(400, 'APPLICATION_INCOMPLETE', 'Bad request.', 'accept: 1 to 4 items.');
      }
      const known = agreementsFor(scenario, terms, standardsAccepted).items;
      for (const item of accept) {
        const document = known.find((candidate) => candidate.documentCode === item.documentCode);
        if (document?.live?.versionLabel !== item.versionLabel) {
          return problem(
            404,
            'WRONG_FROM_STATE',
            'No such document version, or it is superseded.',
            'A newer version has been published.',
          );
        }
      }
      // An item already accepted is a no-op, never a 409 (design 25, 3.1).
      for (const item of accept) {
        if (item.documentCode === AUTHOR_AGREEMENT && !terms.accepted) {
          terms = {
            ...terms,
            accepted: true,
            acceptedAt: FIXTURE_ACCEPTED_AT,
            blocksSubmission: false,
          };
        }
        if (item.documentCode === EDITORIAL_STANDARDS) {
          standardsAccepted = true;
        }
      }
      return { status: 201, body: agreementsFor(scenario, terms, standardsAccepted) };
    }

    return problem(
      404,
      'WRONG_FROM_STATE',
      'Not found.',
      `The fixture transport has no answer for ${call.method} ${call.path}.`,
    );
  };
}

/* ====================================================== design 25's agreements, at the foot */

const AUTHOR_AGREEMENT = 'creator_program';
const EDITORIAL_STANDARDS = 'editorial_standards';
const FIXTURE_STANDARDS_URL = 'https://moonx.app/legal/editorial-standards/2026-10-11';
const FIXTURE_ACCEPTED_AT = '2026-08-27T12:00:00Z';

/**
 * `GET /v1/agreements`, in the documents' order: the editorial standards and the author agreement.
 * A fresh box has no version of anything in force, so nothing is listed there. The titles are the
 * cabinet's words; the screens name the documents they know by code (../agreements.ts).
 */
function agreementsFor(
  scenario: FixtureScenario,
  terms: TermsState,
  standardsAccepted: boolean,
): AgreementList {
  if (scenario === 'fresh-box') {
    return { items: [] };
  }
  const acceptedAt = terms.acceptedAt;
  return {
    items: [
      {
        documentCode: EDITORIAL_STANDARDS,
        title: 'Editorial standards',
        blocks: 'submission',
        live: {
          versionLabel: '2026-10-11',
          effectiveFrom: '2026-10-11T00:00:00Z',
          contentUrl: FIXTURE_STANDARDS_URL,
          isMaterialChange: false,
        },
        accepted: standardsAccepted
          ? {
              versionLabel: '2026-10-11',
              acceptedAt: FIXTURE_ACCEPTED_AT,
              contentUrl: FIXTURE_STANDARDS_URL,
            }
          : null,
        owed: !standardsAccepted,
        newerVersionWaiting: false,
      },
      {
        documentCode: AUTHOR_AGREEMENT,
        title: 'Author agreement',
        blocks: 'submission',
        live: {
          versionLabel: terms.versionLabel,
          effectiveFrom: terms.effectiveFrom,
          contentUrl: terms.contentUrl,
          isMaterialChange: terms.isMaterialChange,
        },
        // The reaccept author accepted an older version, so a newer one is waiting.
        accepted:
          terms.accepted && typeof acceptedAt === 'string'
            ? { versionLabel: terms.versionLabel, acceptedAt, contentUrl: terms.contentUrl }
            : scenario === 'reaccept'
              ? {
                  versionLabel: '2026-01',
                  acceptedAt: '2026-02-11T09:14:00Z',
                  contentUrl: FIXTURE_TERMS_URL,
                }
              : null,
        owed: !terms.accepted,
        newerVersionWaiting: !terms.accepted && scenario === 'reaccept',
      },
    ],
  };
}

/** The request's items, or null when the body is not `{ accept: [{ documentCode, versionLabel }] }`. */
function acceptItemsOf(
  body: unknown,
): readonly { readonly documentCode: string; readonly versionLabel: string }[] | null {
  const accept =
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>).accept : null;
  if (!Array.isArray(accept)) {
    return null;
  }
  const items = accept.map((item: unknown) => {
    const record =
      typeof item === 'object' && item !== null ? (item as Record<string, unknown>) : {};
    return { documentCode: record.documentCode, versionLabel: record.versionLabel };
  });
  return items.every(
    (item) => typeof item.documentCode === 'string' && typeof item.versionLabel === 'string',
  )
    ? (items as { documentCode: string; versionLabel: string }[])
    : null;
}
